<script setup lang="ts">
// Spec 2026-10-12 (FOF backtest) §4.11: the FOF page's Backtest tab. Live = Paolo's Strategy Allocation on
// every fund's live preset; Candidate = an edited table; Results = the candidate, the sensitivity grid and
// the over-fit evidence (CPCV, Deflated Sharpe, paired test vs live, PBO); Promote writes a table to Strategy
// Allocation through the same PUT as the Strategy card, with a confirmation sheet and a Revert.
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { api, type FofBacktestResponse, type FofTableResult, type PotRow, type FofGridSpec, type FundAllocation } from '../../api/client'
import { potRowsError } from '../strategy/allocationModel'
import FundInPotBreakdown from './FundInPotBreakdown.vue'
import { equityChart } from '../japan/sharedPotChart'
import {
  rowDiff, failedGates, rankedTables, candidateFromLive, changeLabel, sensitivitySeries, spanLimitText, laggingInputsText,
  eligibilityOf, mechanismOf, signedPct, blocksText,
} from './fofBacktestModel'

const POLL_MS = 5000

const data = ref<FofBacktestResponse | null>(null)
const loaded = ref(false)
const loadError = ref<string | null>(null)
const actionError = ref<string | null>(null)
const busy = ref(false)
const candidate = ref<PotRow[]>([])
const gridMode = ref<FofGridSpec['mode']>('oat')
const pairA = ref('')
const pairB = ref('')
const selectedId = ref<string | null>(null)
const confirmFor = ref<FofTableResult | null>(null)
const ack = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

function isResponse(v: unknown): v is FofBacktestResponse {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && 'liveRows' in v
}

async function load(): Promise<void> {
  try {
    const r: unknown = await api.fofBacktest()
    data.value = isResponse(r) ? r : null
    loadError.value = null
    if (data.value && candidate.value.length === 0) candidate.value = candidateFromLive(data.value.liveRows)
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : String(e)
  } finally {
    loaded.value = true
    const inFlight = data.value?.inFlight != null
    if (inFlight && timer === null) timer = setInterval(() => { void load() }, POLL_MS)
    if (!inFlight && timer !== null) { clearInterval(timer); timer = null }
  }
}

onMounted(() => { void load(); clockTimer = setInterval(() => { now.value = Date.now() }, 60_000) })
onUnmounted(() => { if (timer !== null) clearInterval(timer); if (clockTimer !== null) clearInterval(clockTimer) })

const report = computed(() => data.value?.latest?.report ?? null)

// Fund breakdown (2026-10-13): what each fund contributed inside the pot, for the Live or Candidate table.
// Only those two tables carry it; a report saved before 2026-10-13 has none (the section asks for a re-run).
const breakdownOf = ref<'Live' | 'Candidate'>('Live')
const candidateTable = computed(() => report.value?.tables.find(t => t.kind === 'Candidate') ?? null)
const breakdownFunds = computed(() => {
  const t = report.value?.tables.find(x => x.kind === breakdownOf.value)
  return t?.funds && t.funds.length > 0 ? t.funds : null
})
// A new run (possibly without a candidate) must never leave the toggle on a missing table. Keyed on the job,
// not the report object: polling during a run replaces the object every 5 s with the same report.
watch(() => data.value?.latest?.jobId, () => { breakdownOf.value = 'Live' })
const ranked = computed(() => (report.value ? rankedTables(report.value) : []))
const spanLimitLine = computed(() => (report.value ? spanLimitText(report.value) : null))
const laggingLine = computed(() => (report.value ? laggingInputsText(report.value) : null))
const liveResult = computed(() => report.value?.tables.find(t => t.kind === 'Live') ?? null)
const presetOf = (fund: string) => data.value?.presetsNow.find(p => p.fundId === fund)?.presetName ?? '—'
// Spec 2026-10-31: the mechanism next to the outcome. A table may not raise a fund whose stale-pricing edge is not
// there (Red, under 10 %/yr, or no history) — shown per fund here and per table below, so Promote is never a surprise.
const mech = computed(() => data.value?.mechanism ?? null)
const elig = (fund: string) => eligibilityOf(mech.value, fund)
const tableMech = (t: FofTableResult) => mechanismOf(mech.value, t.id)
const blockedTitle = (t: FofTableResult) => blocksText(tableMech(t))

const candidateError = computed(() => potRowsError(candidate.value.map((r): FundAllocation => ({
  fund: r.fundId as FundAllocation['fund'], enabled: r.enabled, weightPct: r.weightPct, tier: r.tier, reservePct: r.reservePct,
}))))
const pairError = computed(() => gridMode.value === 'pair' && (!pairA.value || !pairB.value || pairA.value === pairB.value)
  ? 'Pick two different funds' : null)
// Spec §4.11: Run is disabled inside 07:30–09:30 UK (the server refuses with 409 there too).
const now = ref(Date.now())
let clockTimer: ReturnType<typeof setInterval> | null = null
const ukHm = computed(() => new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
}).format(new Date(now.value)))
const inRunWindow = computed(() => ukHm.value >= '07:30' && ukHm.value < '09:30')
const canRun = computed(() => !!data.value?.canRun && !busy.value && !candidateError.value && !pairError.value
  && data.value?.inFlight == null && !inRunWindow.value)

function num(v: unknown): number | null {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
function setField(fund: string, field: 'enabled' | 'weightPct' | 'tier' | 'reservePct', v: unknown): void {
  candidate.value = candidate.value.map(r => {
    if (r.fundId !== fund) return r
    if (field === 'enabled') return { ...r, enabled: Boolean(v) }
    if (field === 'tier') return { ...r, tier: num(v) }
    return { ...r, [field]: num(v) ?? 0 }
  })
}
function resetToLive(): void { if (data.value) candidate.value = candidateFromLive(data.value.liveRows) }
function use(t: FofTableResult): void { candidate.value = candidateFromLive(t.rows); selectedId.value = t.id }

async function run(): Promise<void> {
  busy.value = true; actionError.value = null
  try {
    const grid: FofGridSpec = gridMode.value === 'pair' ? { mode: 'pair', funds: [pairA.value, pairB.value] } : { mode: gridMode.value }
    await api.fofBacktestRun({ candidate: candidate.value, grid })
    await load()
  } catch (e) { actionError.value = e instanceof Error ? e.message : String(e) } finally { busy.value = false }
}

function openConfirm(t: FofTableResult): void { confirmFor.value = t; ack.value = false; actionError.value = null }
const confirmDiff = computed(() => (confirmFor.value && data.value ? rowDiff(data.value.liveRows, confirmFor.value.rows) : []))
const confirmFailed = computed(() => (confirmFor.value ? failedGates(confirmFor.value) : []))
const confirmMech = computed(() => (confirmFor.value ? tableMech(confirmFor.value) : null))
const confirmBlocked = computed(() => blocksText(confirmMech.value))
const canConfirm = computed(() => !busy.value && !confirmBlocked.value && (confirmFailed.value.length === 0 || ack.value))

async function promote(): Promise<void> {
  const t = confirmFor.value
  const jobId = data.value?.latest?.jobId
  if (!t || !jobId) return
  busy.value = true; actionError.value = null
  try {
    await api.fofBacktestPromote({ jobId, table: t.id, acknowledgeFailedGates: confirmFailed.value.length > 0 && ack.value })
    confirmFor.value = null
    await load()
    resetToLive()
  } catch (e) { actionError.value = e instanceof Error ? e.message : String(e) } finally { busy.value = false }
}

async function cancelRun(): Promise<void> {
  busy.value = true; actionError.value = null
  try { await api.fofBacktestCancel(); await load() }
  catch (e) { actionError.value = e instanceof Error ? e.message : String(e) } finally { busy.value = false }
}

// Review minor #15: the server refuses a stale promote (409); say so on the button instead.
const STALE_TITLE = 'Your allocation or a live preset changed since this run. Run again before promoting.'

async function revert(): Promise<void> {
  const jobId = data.value?.promotedJobId
  if (!jobId) return
  busy.value = true; actionError.value = null
  try { await api.fofBacktestRevert({ jobId }); await load(); resetToLive() }
  catch (e) { actionError.value = e instanceof Error ? e.message : String(e) } finally { busy.value = false }
}

const selected = computed(() => report.value?.tables.find(t => t.id === selectedId.value) ?? null)
const chart = computed(() => {
  const r = report.value
  if (!r) return null
  const series = [liveResult.value?.equity, r.tables.find(t => t.kind === 'Candidate')?.equity, selected.value?.equity]
    .filter((s): s is number[] => Array.isArray(s) && s.length === r.dates.length)
  return series.length ? equityChart(r.dates, series) : null
})

// Spec §4.11: one-at-a-time sensitivity per fund — x = the weight tried, y = OOS CAGR (line) and
// CPCV median Sharpe (dots), each scaled to its own range.
const SENS_W = 300, SENS_H = 120, PAD = 24
const sensitivity = computed(() => {
  const r = report.value
  if (!r) return []
  const funds = [...new Set(r.tables.filter(t => t.id.startsWith('oat:') && t.id.includes(':w')).map(t => t.id.split(':')[1]))]
  return funds.map(fund => {
    const pts = sensitivitySeries(r, fund, 'w')
    if (pts.length < 2) return null
    const xs = pts.map(p => p.x), cs = pts.map(p => p.cagr), ss = pts.map(p => p.sharpe)
    const sx = (v: number) => PAD + ((v - Math.min(...xs)) / ((Math.max(...xs) - Math.min(...xs)) || 1)) * (SENS_W - 2 * PAD)
    const sy = (v: number, a: number[]) => SENS_H - PAD - ((v - Math.min(...a)) / ((Math.max(...a) - Math.min(...a)) || 1)) * (SENS_H - 2 * PAD)
    return {
      fund, pts,
      cagrLine: pts.map(p => `${sx(p.x).toFixed(1)},${sy(p.cagr, cs).toFixed(1)}`).join(' '),
      sharpeDots: pts.map(p => ({ x: sx(p.x), y: sy(p.sharpe, ss) })),
      ticks: pts.map(p => ({ x: sx(p.x), label: `${p.x}` })),
      cagrRange: `${Math.min(...cs).toFixed(0)}–${Math.max(...cs).toFixed(0)} %`,
      sharpeRange: `${Math.min(...ss).toFixed(2)}–${Math.max(...ss).toFixed(2)}`,
    }
  }).filter((x): x is NonNullable<typeof x> => x !== null)
})

function pct(v: number | null | undefined, dp = 1): string { return v == null ? '—' : `${v.toFixed(dp)} %` }
function n2(v: number | null | undefined): string { return v == null ? '—' : v.toFixed(2) }
function gateClass(v: boolean | null): string { return v === null ? 'gate na' : v ? 'gate pass' : 'gate fail' }
</script>

<template>
  <section class="fof-backtest" data-testid="fof-backtest-panel">
    <h2>Fund of funds backtest</h2>
    <p class="fb-note">Your Strategy Allocation on every fund's live preset, a sensitivity grid around a candidate, and
      over-fit checks before anything is promoted.</p>

    <p v-if="loadError" class="fb-error" data-testid="fof-bt-error">{{ loadError }}</p>
    <p v-if="actionError" class="fb-error" data-testid="fof-bt-action-error">{{ actionError }}</p>
    <p v-if="data?.stale" class="fb-warn" data-testid="fof-bt-stale">Stale — your allocation or a fund's live preset
      changed since this run. Run again before promoting.</p>
    <div v-if="data?.inFlight" class="fb-running">
      <p class="fb-status" data-testid="fof-bt-running">Running: {{ data.inFlight.phase || data.inFlight.status }}…</p>
      <button type="button" class="fb-btn sm" data-testid="fof-bt-cancel" :disabled="busy" @click="cancelRun">Cancel run</button>
    </div>
    <p v-if="data?.lastFailure" class="fb-error" data-testid="fof-bt-failure">Last run failed: {{ data.lastFailure.error }}</p>

    <!-- Live -->
    <section v-if="data" class="fb-card" data-testid="fof-bt-live">
      <h3>Live allocation</h3>
      <table class="fb-table">
        <thead><tr><th>Fund</th><th>On</th><th>Weight</th><th>Tier</th><th>Reserve</th><th>Stale %/yr</th><th>Raise</th><th>Live preset</th></tr></thead>
        <tbody>
          <tr v-for="r in data.liveRows" :key="r.fundId">
            <td>{{ r.fundId }}</td><td>{{ r.enabled ? 'on' : 'off' }}</td><td>{{ r.weightPct }}</td>
            <td>{{ r.tier ?? '—' }}</td><td>{{ r.reservePct }}</td>
            <td>{{ elig(r.fundId)?.stalePctYr == null ? '—' : elig(r.fundId)!.stalePctYr!.toFixed(1) }}</td>
            <td :data-testid="`fof-bt-elig-${r.fundId}`">
              <span v-if="!mech" class="gate na" title="Mechanism data unavailable; Promote re-checks">?</span>
              <span v-else-if="elig(r.fundId)?.eligible" class="gate pass" title="May be raised">OK</span>
              <span v-else class="gate fail" :title="elig(r.fundId)?.reason ?? 'no edge history'">No</span>
            </td>
            <td class="preset">{{ presetOf(r.fundId) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-if="liveResult" class="fb-note">Live table: OOS CAGR {{ pct(liveResult.oos.cagrPct) }} · Sharpe
        {{ n2(liveResult.oos.sharpe) }} · max DD {{ pct(liveResult.oos.maxDdPct) }}</p>
      <ul v-if="mech && mech.funds.some(f => !f.eligible)" class="fb-elig-list" data-testid="fof-bt-elig-reasons">
        <li v-for="f in mech.funds.filter(x => !x.eligible)" :key="f.fundId">{{ f.fundId }} can't be raised: {{ f.reason }}</li>
      </ul>
      <p v-if="mech" class="fb-note">Raise = whether a promoted table may raise the fund's weight: not Red on the
        pricing-source detector and at least 10 %/yr from stale pricing over 250 days (edge map, as of
        {{ mech.edgeAsOf ?? '—' }}). Cutting a weight is never blocked.</p>
      <button v-if="data.promotedJobId" type="button" class="fb-btn" data-testid="fof-bt-revert" :disabled="busy"
              @click="revert">Revert last promote</button>
    </section>

    <!-- Candidate -->
    <section v-if="data" class="fb-card" data-testid="fof-bt-candidate">
      <h3>Candidate</h3>
      <div class="fb-cand">
        <div v-for="r in candidate" :key="r.fundId" class="fb-cand-row">
          <span class="fund">{{ r.fundId }}<span v-if="mech && !elig(r.fundId)?.eligible" class="gate fail"
            :title="`Can't be raised: ${elig(r.fundId)?.reason ?? 'no edge history'}`">no raise</span></span>
          <label><input type="checkbox" :checked="r.enabled" :data-testid="`fof-bt-cand-on-${r.fundId}`"
                        @change="setField(r.fundId, 'enabled', ($event.target as HTMLInputElement).checked)" /> on</label>
          <label>Weight <input type="number" min="0" max="100" :value="r.weightPct" :data-testid="`fof-bt-cand-weight-${r.fundId}`"
                               @input="setField(r.fundId, 'weightPct', ($event.target as HTMLInputElement).value)" /></label>
          <label>Tier <input type="number" min="1" max="9" :value="r.tier ?? ''" :data-testid="`fof-bt-cand-tier-${r.fundId}`"
                             @input="setField(r.fundId, 'tier', ($event.target as HTMLInputElement).value)" /></label>
          <label>Reserve <input type="number" min="0" max="50" :value="r.reservePct" :data-testid="`fof-bt-cand-reserve-${r.fundId}`"
                                @input="setField(r.fundId, 'reservePct', ($event.target as HTMLInputElement).value)" /></label>
        </div>
      </div>
      <p v-if="candidateError" class="fb-error" data-testid="fof-bt-cand-error">{{ candidateError }}</p>
      <div class="fb-grid" role="radiogroup" aria-label="Sensitivity grid">
        <label><input v-model="gridMode" type="radio" value="oat" /> One at a time</label>
        <label><input v-model="gridMode" type="radio" value="pair" /> Pair</label>
        <label><input v-model="gridMode" type="radio" value="none" /> None</label>
        <template v-if="gridMode === 'pair'">
          <select v-model="pairA" data-testid="fof-bt-pair-a"><option value="">—</option><option v-for="r in candidate" :key="r.fundId" :value="r.fundId">{{ r.fundId }}</option></select>
          <select v-model="pairB" data-testid="fof-bt-pair-b"><option value="">—</option><option v-for="r in candidate" :key="r.fundId" :value="r.fundId">{{ r.fundId }}</option></select>
        </template>
      </div>
      <p v-if="pairError" class="fb-error">{{ pairError }}</p>
      <div class="fb-actions">
        <button type="button" class="fb-btn" @click="resetToLive">Reset to live</button>
        <button type="button" class="fb-btn run" data-testid="fof-bt-run" :disabled="!canRun" @click="run">
          {{ busy ? 'Starting…' : 'Run' }}</button>
      </div>
      <p v-if="inRunWindow" class="fb-warn" data-testid="fof-bt-run-window">Runs are paused 07:30–09:30 UK while the live
        dealing plan is built and executed (UK time now {{ ukHm }}).</p>
      <p class="fb-note">Runs are refused 07:30–09:30 UK. Promote and revert are also refused 07:00–08:30 UTC on weekdays.</p>
    </section>

    <!-- Results -->
    <section v-if="report" class="fb-card" data-testid="fof-bt-results">
      <h3>Results</h3>
      <p class="fb-note">{{ report.spanStart }} → {{ report.spanEnd }} · OOS from {{ report.oosFrom }} · grid {{ report.gridMode }} ·
        tables tried since last promote {{ report.trialCount }} ·
        PBO {{ report.pbo == null ? 'n/a' : report.pbo.toFixed(2) }}</p>
      <p v-if="spanLimitLine" :class="report.spanLimit?.stale ? 'fb-warn' : 'fb-note'" data-testid="fof-bt-span-limit">
        {{ spanLimitLine }}</p>
      <p v-if="laggingLine" class="fb-warn" data-testid="fof-bt-lagging">{{ laggingLine }}</p>
      <div class="fb-scroll">
        <table class="fb-table results">
          <thead><tr>
            <th>#</th><th>Change</th><th>OOS CAGR</th><th>OOS Sharpe</th><th>Max DD</th>
            <th title="Last 250 sessions of the same run">250d Sharpe</th><th>250d CAGR</th><th>250d DD</th>
            <th>CPCV med / min</th><th>+ windows</th>
            <th>DSR (p)</th><th>ΔSharpe [95 %]</th><th>Gates</th>
            <th title="Weighted stale-pricing / continuation return, %/yr; Δ vs live">Stale / trend</th>
            <th class="row-actions"></th>
          </tr></thead>
          <tbody>
            <tr v-for="t in ranked" :key="t.id" data-testid="fof-bt-row" :data-id="t.id"
                :class="{ live: t.kind === 'Live', sel: t.id === selectedId, err: t.error }">
              <td>{{ t.rank }}</td>
              <td class="label">{{ changeLabel(t) }}<span v-if="t.changedFields > 1" class="multi"> ({{ t.changedFields }} changes)</span></td>
              <template v-if="!t.error">
                <td>{{ pct(t.oos.cagrPct) }}</td><td>{{ n2(t.oos.sharpe) }}</td><td>{{ pct(t.oos.maxDdPct) }}</td>
                <td>{{ n2(t.recent?.sharpe) }}</td><td>{{ pct(t.recent?.cagrPct) }}</td><td>{{ pct(t.recent?.maxDdPct) }}</td>
                <td>{{ n2(t.cpcvMedianSharpe) }} / {{ n2(t.cpcvMinSharpe) }}</td><td>{{ t.cpcvPositive }}/10</td>
                <td>{{ n2(t.dsr) }} ({{ t.dsrPValue.toFixed(3) }})</td>
                <td>{{ t.deltaSharpe == null ? '—' : `${n2(t.deltaSharpe)} [${n2(t.deltaLo)}, ${n2(t.deltaHi)}]` }}</td>
                <td class="gates">
                  <span :class="gateClass(t.gates.dsr)" title="Deflated Sharpe > 1.645">DSR</span>
                  <span :class="gateClass(t.gates.cpcv)" title="All 10 CPCV windows positive">CPCV</span>
                  <span :class="gateClass(t.kind === 'Live' ? null : t.gates.paired)" title="Paired ΔSharpe interval above 0">Δ</span>
                  <span :class="gateClass(t.gates.pbo)" title="Grid PBO < 0.25">PBO</span>
                </td>
                <td class="mech" :data-testid="`fof-bt-mech-${t.id}`">
                  <template v-if="tableMech(t)">
                    {{ tableMech(t)!.stalePctYr.toFixed(1) }} / {{ tableMech(t)!.continuationPctYr.toFixed(1) }}
                    <span v-if="t.kind !== 'Live'" class="delta">({{ signedPct(tableMech(t)!.deltaStalePctYr) }} / {{ signedPct(tableMech(t)!.deltaContinuationPctYr) }})</span>
                    <span v-if="tableMech(t)!.trendPaid" class="gate warn" title="The change over live comes more from continuation (trend) than stale pricing">trend-paid</span>
                    <span v-if="tableMech(t)!.blocks.length" class="gate fail" :title="blockedTitle(t) ?? undefined">blocked</span>
                  </template>
                  <template v-else>—</template>
                </td>
              </template>
              <td v-else colspan="12" class="fb-error-cell">{{ t.error }}</td>
              <td class="row-actions"><div>
                <button type="button" class="fb-btn sm" data-testid="fof-bt-use" @click="use(t)">Use</button>
                <button v-if="t.kind !== 'Live' && !t.error" type="button" class="fb-btn sm promote" data-testid="fof-bt-promote"
                        :disabled="data?.stale || !!blockedTitle(t)" :title="data?.stale ? STALE_TITLE : (blockedTitle(t) ?? undefined)"
                        @click="openConfirm(t)">Promote</button>
              </div></td>
            </tr>
          </tbody>
        </table>
      </div>
      <svg v-if="chart" :viewBox="chart.viewBox" class="fb-svg" role="img" aria-label="Equity: live, candidate, selected">
        <line v-for="tk in chart.yTicks" :key="`g${tk.label}`" x1="48" x2="592" :y1="tk.pos" :y2="tk.pos" class="grid" />
        <text v-for="tk in chart.yTicks" :key="`y${tk.label}`" x="44" :y="tk.pos + 3" class="tick" text-anchor="end">{{ tk.label }}</text>
        <text v-for="tk in chart.xTicks" :key="`x${tk.label}`" :x="tk.pos" y="216" class="tick">{{ tk.label }}</text>
        <polyline v-for="(l, i) in chart.lines" :key="i" :points="l" :class="['line', `s${i}`]" />
      </svg>
      <div v-if="sensitivity.length" class="fb-sens-grid">
        <figure v-for="sv in sensitivity" :key="sv.fund" class="fb-sens" :data-testid="`fof-bt-sens-${sv.fund}`">
          <figcaption>{{ sv.fund }} weight → OOS CAGR {{ sv.cagrRange }} (line) · CPCV median Sharpe {{ sv.sharpeRange }} (dots)</figcaption>
          <svg :viewBox="`0 0 ${SENS_W} ${SENS_H}`" class="fb-svg" role="img" :aria-label="`${sv.fund} weight sensitivity`">
            <polyline :points="sv.cagrLine" class="line s1" />
            <circle v-for="(d, i) in sv.sharpeDots" :key="i" :cx="d.x" :cy="d.y" r="3" class="dot" />
            <text v-for="tk in sv.ticks" :key="tk.label" :x="tk.x" :y="SENS_H - 6" class="tick" text-anchor="middle">{{ tk.label }}</text>
          </svg>
        </figure>
      </div>
    </section>
    <p v-else-if="loaded && data" class="fb-note" data-testid="fof-bt-empty">No FOF backtest has been run yet.</p>

    <section v-if="report" class="fb-card" data-testid="fof-bt-breakdown">
      <h3>Fund breakdown</h3>
      <div class="fb-toggle" role="tablist">
        <button type="button" role="tab" data-testid="fof-bt-breakdown-live" :aria-selected="breakdownOf === 'Live'"
                :class="{ on: breakdownOf === 'Live' }" @click="breakdownOf = 'Live'">Live</button>
        <button v-if="candidateTable" type="button" role="tab" data-testid="fof-bt-breakdown-candidate"
                :aria-selected="breakdownOf === 'Candidate'" :class="{ on: breakdownOf === 'Candidate' }"
                @click="breakdownOf = 'Candidate'">Candidate</button>
      </div>
      <FundInPotBreakdown v-if="breakdownFunds" :funds="breakdownFunds" :dates="report.dates" />
      <p v-else class="fb-note">Re-run the backtest to see the fund breakdown.</p>
    </section>

    <!-- Promote confirmation -->
    <div v-if="confirmFor" class="fb-sheet" role="dialog" aria-modal="true" data-testid="fof-bt-confirm">
      <h3>Promote “{{ changeLabel(confirmFor) }}” to Strategy Allocation?</h3>
      <ul class="fb-diff">
        <li v-for="d in confirmDiff" :key="`${d.fundId}-${d.field}`">{{ d.fundId }} {{ d.field }}: {{ d.from }} → {{ d.to }}</li>
        <li v-if="confirmDiff.length === 0">No change from live.</li>
      </ul>
      <table class="fb-table">
        <thead><tr><th></th><th>Full</th><th>OOS</th><th>Last 250d</th></tr></thead>
        <tbody>
          <tr><td>Sharpe</td><td>{{ n2(confirmFor.full.sharpe) }}</td><td>{{ n2(confirmFor.oos.sharpe) }}</td><td>{{ n2(confirmFor.recent?.sharpe) }}</td></tr>
          <tr><td>CAGR</td><td>{{ pct(confirmFor.full.cagrPct) }}</td><td>{{ pct(confirmFor.oos.cagrPct) }}</td><td>{{ pct(confirmFor.recent?.cagrPct) }}</td></tr>
          <tr><td>Max DD</td><td>{{ pct(confirmFor.full.maxDdPct) }}</td><td>{{ pct(confirmFor.oos.maxDdPct) }}</td><td>{{ pct(confirmFor.recent?.maxDdPct) }}</td></tr>
        </tbody>
      </table>
      <p v-if="confirmMech" class="fb-note" data-testid="fof-bt-confirm-mech">Where the return comes from (250 days, %/yr):
        stale pricing {{ confirmMech.stalePctYr.toFixed(1) }} ({{ signedPct(confirmMech.deltaStalePctYr) }} vs live),
        trend {{ confirmMech.continuationPctYr.toFixed(1) }} ({{ signedPct(confirmMech.deltaContinuationPctYr) }}),
        other {{ confirmMech.otherPctYr.toFixed(1) }}.<template v-if="confirmMech.trendPaid"> Trend-paid: the gain over live
        comes more from trend than from stale pricing, so it moves with the other trend funds.</template></p>
      <p v-if="confirmBlocked" class="fb-error" data-testid="fof-bt-confirm-blocked">Can't promote. {{ confirmBlocked }}
        Keep or lower those weights.</p>
      <p v-if="confirmFailed.length === 0" class="fb-ok">Passes every over-fit gate.</p>
      <template v-else>
        <p class="fb-warn">Fails: {{ confirmFailed.join(', ') }}.</p>
        <label class="fb-ack"><input v-model="ack" type="checkbox" data-testid="fof-bt-ack" /> I accept the over-fit risk and want to promote anyway.</label>
      </template>
      <div class="fb-actions">
        <button type="button" class="fb-btn" @click="confirmFor = null">Cancel</button>
        <button type="button" class="fb-btn run" data-testid="fof-bt-confirm-go" :disabled="!canConfirm" @click="promote">Promote</button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.fof-backtest { padding: 1rem; background: #121212; border-radius: 4px; color: #ccc; }
.fof-backtest h2 { margin: 0 0 0.5rem; font-size: 1.1rem; color: #eee; }
.fb-card { background: #1a1a1a; border: 1px solid #333; border-radius: 4px; padding: 0.75rem; margin-bottom: 0.75rem; min-width: 0; }
.fb-card h3, .fb-sheet h3 { color: #aaa; font-size: 0.8rem; margin: 0 0 0.5rem; text-transform: uppercase; letter-spacing: 1px; }
.fb-note { font-size: 0.75rem; color: #888; line-height: 1.4; margin: 0.25rem 0 0.75rem; }
.fb-toggle { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; }
.fb-toggle button { flex: 1; min-height: 44px; background: #222; color: #ccc; border: 1px solid #444; border-radius: 4px; cursor: pointer; }
.fb-toggle button.on { background: #37474f; color: #fff; border-color: #607d8b; }
.fb-error { color: #f87171; background: #2a1010; border: 1px solid #7f1d1d; border-radius: 4px; padding: 0.5rem; font-size: 0.85rem; }
.fb-warn { color: #fbbf24; background: #2a2210; border: 1px solid #7f6a1d; border-radius: 4px; padding: 0.5rem; font-size: 0.85rem; }
.fb-ok { color: #81c784; font-size: 0.85rem; }
.fb-status { color: #b8860b; font-size: 0.85rem; }
.fb-scroll { overflow-x: auto; }
.fb-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; font-variant-numeric: tabular-nums; }
.fb-table th, .fb-table td { padding: 0.35rem 0.4rem; text-align: right; border-bottom: 1px solid #2a2a2a; white-space: nowrap; }
.fb-table th:first-child, .fb-table td:first-child, .fb-table td.label, .fb-table td.preset { text-align: left; }
.fb-table tr.live td { color: #90caf9; }
.fb-table tr.sel td { background: #1f2a33; }
.multi { color: #fbbf24; font-size: 0.75rem; }
.gate { display: inline-block; padding: 0 0.3rem; margin-left: 0.2rem; border-radius: 3px; font-size: 0.7rem; }
.gate.pass { background: #1b5e20; color: #c8e6c9; }
.gate.fail { background: #7f1d1d; color: #fecaca; }
.gate.na { background: #333; color: #999; }
.gate.warn { background: #5c4a0f; color: #fde68a; }
.fb-table td.mech { text-align: left; }
.mech .delta { color: #999; font-size: 0.75rem; }
.fb-elig-list { margin: 0 0 0.5rem; padding-left: 1.2rem; font-size: 0.8rem; color: #fca5a5; overflow-wrap: anywhere; }
.fb-error-cell { color: #f87171; text-align: left !important; }
.fb-cand { display: grid; gap: 0.5rem; }
.fb-cand-row { display: grid; grid-template-columns: 6rem repeat(4, minmax(0, 1fr)); gap: 0.5rem; align-items: center; font-size: 0.85rem; }
.fb-cand-row input[type="number"] { width: 4.5rem; min-height: 36px; background: #111; color: #eee; border: 1px solid #444; border-radius: 4px; padding: 0 0.3rem; }
.fb-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; margin: 0.75rem 0; font-size: 0.85rem; align-items: center; }
.fb-grid select { min-height: 36px; background: #111; color: #eee; border: 1px solid #444; border-radius: 4px; }
.fb-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.5rem; }
.fb-btn { min-height: 44px; padding: 0 1rem; background: #222; color: #ccc; border: 1px solid #444; border-radius: 4px; cursor: pointer; }
.fb-btn.run { background: #2e7d32; color: #fff; border: none; font-weight: 600; }
.fb-btn.sm { min-height: 32px; padding: 0 0.6rem; font-size: 0.8rem; }
.fb-btn.promote { border-color: #4a90d9; color: #90caf9; }
.fb-btn:disabled { opacity: 0.5; cursor: default; }
.fb-running { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
/* The actions column stays pinned to the right edge of the scrolling results table, so Use/Promote are
   always on screen (2026-10-13, Paolo: they were hidden behind the horizontal scroller). */
.fb-table .row-actions { position: sticky; right: 0; background: #1a1a1a; box-shadow: -6px 0 6px -4px rgba(0, 0, 0, 0.7); }
.fb-table tr.sel .row-actions { background: #1f2a33; }
.row-actions > div { display: flex; gap: 0.3rem; justify-content: flex-end; }
.fb-svg { width: 100%; height: auto; display: block; margin-top: 0.75rem; }
.fb-svg .grid { stroke: #2a2a2a; stroke-width: 1; }
.fb-svg .tick { fill: #888; font-size: 10px; }
.fb-svg .line { fill: none; stroke-width: 1.5; }
.fb-svg .line.s0 { stroke: #90caf9; }
.fb-svg .line.s1 { stroke: #4caf50; }
.fb-svg .line.s2 { stroke: #fbbf24; }
.fb-svg .dot { fill: #fbbf24; }
.fb-sens-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 0.75rem; margin-top: 0.75rem; }
.fb-sens { margin: 0; }
.fb-sens figcaption { font-size: 0.75rem; color: #999; margin-bottom: 0.25rem; }
.fb-sheet { position: fixed; inset: auto 0 0 0; max-height: 85vh; overflow-y: auto; background: #1a1a1a; border-top: 2px solid #4a90d9;
  padding: 1rem; z-index: 50; box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.6); }
.fb-diff { margin: 0 0 0.75rem; padding-left: 1.2rem; font-size: 0.9rem; color: #eee; }
.fb-ack { display: flex; gap: 0.5rem; align-items: center; font-size: 0.85rem; margin: 0.5rem 0; }
@media (max-width: 639px) {
  .fb-cand-row { grid-template-columns: 1fr 1fr; }
  .fb-cand-row .fund { grid-column: 1 / -1; font-weight: 600; color: #eee; }
}
/* Desktop: break out of the app's 960px column and use the full window width (2026-10-13, Paolo).
   The 1.5rem side gutters leave room for a vertical scrollbar, so the page never scrolls sideways. */
@media (min-width: 1100px) {
  .fof-backtest { width: calc(100vw - 3rem); margin-left: calc(50% - 50vw + 1.5rem); box-sizing: border-box; }
  .fb-cand { grid-template-columns: repeat(auto-fill, minmax(460px, 1fr)); column-gap: 1.5rem; }
}
@media (min-width: 640px) {
  .fb-sheet { inset: 10vh auto auto 50%; transform: translateX(-50%); width: min(640px, 92vw); border: 1px solid #4a90d9; border-radius: 6px; }
}
</style>
